import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import {
  isParagonOnlyProvider,
  NativeProviderType,
} from "components/integrations/providerMetadata";
import { AuthMode, FindProviderResponse } from "lib/generated/integration-api";
import { useIntegrationApi } from "lib/integrationApiContext";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

type QueryKey = [string];

/**
 * The Paragon-only CRMs are filtered out below, so the provider that reaches a caller is always one
 * with native UI. Saying so in the type is what lets those callers index the provider-keyed maps
 * directly.
 */
export type NativeProviderResponse = Omit<FindProviderResponse, "providerType"> & {
  providerType?: NativeProviderType;
};

/**
 * A Paragon-backed install must not drive the native CRM UI — provider copy, icons, the
 * server-built form, the setup page — none of which it has. HUBSPOT shares its type name with
 * native installs, so the auth mode is what tells them apart; Dynamics and Zoho are only ever
 * Paragon-backed and are screened by type as well, which is what narrows the return type.
 */
function isParagonBacked(response: FindProviderResponse): boolean {
  return response.authMode === AuthMode.Paragon;
}

/**
 * The company's connected CRM, exactly as the service reports it.
 *
 * One query behind all three hooks below: they differ only in which installs they are willing to
 * report, which is a view over the same response rather than a second request. Narrowing through
 * `select` also keeps each hook's rule in one readable place.
 */
function useCrmProviderQuery<TResult>(
  select: (response: FindProviderResponse) => TResult,
  options?: UseQueryOptions<FindProviderResponse, unknown, TResult, QueryKey>,
) {
  const api = useIntegrationApi();
  return useQuery(["integrationProvider"], () => api.fetchProvider(), {
    ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
    select,
    ...options,
  });
}

/**
 * The company's connected CRM as the native UI understands it: a Paragon-backed install is
 * reported as no provider, because the surfaces keyed off this hook cannot render it — the
 * components that can read the connection from Paragon instead.
 */
export function useFindIntegrationProvider(
  options?: UseQueryOptions<FindProviderResponse, unknown, NativeProviderResponse, QueryKey>,
) {
  return useCrmProviderQuery<NativeProviderResponse>((response) => {
    if (isParagonBacked(response) || isParagonOnlyProvider(response.providerType)) {
      return { ...response, providerType: undefined };
    }
    // Rebuilt rather than returned as-is so the narrowing just done reaches the return type;
    // narrowing a property does not narrow the object holding it.
    return { ...response, providerType: response.providerType };
  }, options);
}

/**
 * The company's connected CRM as the contact-send surfaces understand it.
 *
 * Identical to the native-UI hook today: a Paragon install pushes a contact from the company's own
 * field mapping, which none of the provider-keyed UI here can drive — it has no idea which fields
 * that mapping names. `useConnectedParagonCrm` answers for those surfaces instead.
 *
 * Kept separate rather than merged because the two answer different questions, and contacts have
 * diverged from opportunities before: this one's rule is what a contact push can render.
 */
export function useContactCrmProvider(
  options?: UseQueryOptions<FindProviderResponse, unknown, NativeProviderResponse, QueryKey>,
) {
  return useCrmProviderQuery<NativeProviderResponse>((response) => {
    if (isParagonBacked(response) || isParagonOnlyProvider(response.providerType)) {
      return { ...response, providerType: undefined };
    }
    return { ...response, providerType: response.providerType };
  }, options);
}

/**
 * The company's connected CRM with nothing filtered out — for the surfaces that want the install
 * itself rather than a view of it, notably the send form and its extra-field settings.
 *
 * Deliberately not `useContactCrmProvider`: that one's contract is a contacts-specific view, and
 * it drops providers with no contact push. A send form reading it would silently lose its extras
 * the day that rule grows, and re-prompt for fields it never showed.
 */
export function useParagonCrmProvider(
  options?: UseQueryOptions<FindProviderResponse, unknown, FindProviderResponse, QueryKey>,
) {
  return useCrmProviderQuery<FindProviderResponse>((response) => response, options);
}
